import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  List,
  ListItem,
  MenuItem,
  Select,
} from '@mui/material';
import clsx from 'clsx';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from 'shared/api';
import {
  getLeadStatusClassName,
  getLeadStatusLabel,
  LEAD_STATUSES,
  LeadStatus,
  normalizeLeadStatus,
} from 'shared/model/constants/lead-status';
import { ROUTE_PATHS } from 'shared/model/constants/paths';
import { ButtonCustom } from 'shared/ui/button-custom';
import { Loader } from 'shared/ui/loader';
import { DashboardHeaderBackLink } from 'widgets/dashboard-header-back-link';
import styles from './leads-view.module.scss';

interface Lead {
  id: number;
  documentId: string;
  equipment?: string;
  condition?: string;
  objectType?: string;
  region?: string;
  amount?: string;
  deadline?: string;
  name?: string;
  phone?: string;
  comment?: string;
  type?: 'selection' | 'insurance' | 'leasing';
  template?: string;
  path?: string;
  status?: LeadStatus | string;
  createdAt: string;
  updatedAt: string;
}

export const LeadsView = () => {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();

  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const getLead = async () => {
    if (!uid) return;

    try {
      setLoading(true);
      const response = await apiClient.get(`/leads/${uid}`);
      setLead(response.data.data);
    } catch (error) {
      console.error('Ошибка при загрузке заявки:', error);
      navigate(ROUTE_PATHS.ADMIN_LEADS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLead();
  }, [uid]);

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const deleteLead = async () => {
    if (!lead) return;

    try {
      await apiClient.delete(`/leads/${lead.documentId || lead.id}`);
      setShowDeleteDialog(false);
      navigate(ROUTE_PATHS.ADMIN_LEADS);
    } catch (error) {
      console.error('Ошибка при удалении заявки:', error);
    }
  };

  const handleStatusChange = async (status: LeadStatus) => {
    if (!lead) return;

    try {
      setStatusUpdating(true);
      await apiClient.put(`/leads/${lead.documentId || lead.id}`, {
        data: { status },
      });
      setLead({ ...lead, status });
    } catch (error) {
      console.error('Ошибка при обновлении статуса:', error);
    } finally {
      setStatusUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <Loader />;
  }

  if (!lead) {
    return (
      <Box className={styles.container}>
        <DashboardHeaderBackLink link={ROUTE_PATHS.ADMIN_LEADS} title={'Заявка не найдена'} />
        <Box style={{ textAlign: 'center', marginTop: '50px' }}>
          <p>Заявка не найдена или была удалена</p>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <DashboardHeaderBackLink
        link={ROUTE_PATHS.ADMIN_LEADS}
        title={`Заявка с лендинга #${lead.id || lead.documentId}`}
      />

      <Box>
        <Card className={styles.card}>
          <CardHeader
            title={`Заявка с лендинга #${lead.id || lead.documentId}`}
          />
          <CardContent className={styles.cardContent}>
            <List>
              <ListItem className={styles.sectionHeader}>
                <b>Контактная информация</b>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Имя:</b>
                <span>{lead.name || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Телефон:</b>
                <span>{lead.phone || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Комментарий:</b>
                <span>{lead.comment || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Статус:</b>
                <FormControl size='small' className={styles.statusSelect}>
                  <Select
                    value={normalizeLeadStatus(lead.status)}
                    onChange={(e) => handleStatusChange(e.target.value as LeadStatus)}
                    disabled={statusUpdating}
                    className={clsx(
                      styles.statusSelectField,
                      styles[getLeadStatusClassName(lead.status)],
                    )}
                    renderValue={(value) => getLeadStatusLabel(value)}
                  >
                    {LEAD_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {getLeadStatusLabel(status)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ListItem>

              <ListItem className={styles.sectionHeader}>
                <b>Детали заявки</b>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Тип:</b>
                <span>{lead.type || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Что нужно подобрать:</b>
                <span>{lead.equipment || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Состояние:</b>
                <span>{lead.condition || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Тип объекта:</b>
                <span>{lead.objectType || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Регион:</b>
                <span>{lead.region || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Сумма:</b>
                <span>{lead.amount || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Срок:</b>
                <span>{lead.deadline || '-'}</span>
              </ListItem>

              <ListItem className={styles.sectionHeader}>
                <b>Источник</b>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Шаблон:</b>
                <span>{lead.template || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Страница:</b>
                <span>{lead.path || '-'}</span>
              </ListItem>

              <ListItem className={styles.sectionHeader}>
                <b>Системная информация</b>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Создана:</b>
                <span>{formatDate(lead.createdAt)}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Последнее изменение:</b>
                <span>{formatDate(lead.updatedAt)}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Document ID:</b>
                <span>{lead.documentId || '-'}</span>
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
